import React from 'react';
import { Link, useLoaderData, useLocation, useNavigate, useOutlet } from 'react-router-dom';
import clsx from 'clsx';

import {
  MagnifyingGlassIcon,
  BellIcon,
  ArrowTopRightOnSquareIcon,
  EnvelopeIcon,
  ClipboardDocumentIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { CheckBadgeIcon } from '@heroicons/react/24/solid';
import { getCaraInboundAIEmailAddress } from '@oysterjs/core/email';
import { PageTitle } from '@oysterjs/ui/page';
import { PageOutlet } from '@oysterjs/ui/navigation';
import { Badge } from '@oysterjs/ui/badge';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@oysterjs/ui/table';
import { Input, InputGroup } from '@oysterjs/ui/input';
import { Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Select } from '@oysterjs/ui/select';
import { Await } from '@oysterjs/ui/async';
import { Code } from '@oysterjs/ui/text';
import {
  CaraWorkspaceState,
  EmailFieldsFragment,
  EmailSenderRole,
  EmailThreadConversationFieldsFragment,
  EmailThreadFieldsFragment,
  EmailThreadState,
  ServiceRequestState
} from '~/api/graphql';
import { Spinner } from '@oysterjs/ui/spinner';
import { AnimatePresence } from 'motion/react';
import { MountableSlideout } from '@oysterjs/ui/slideout';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import Markdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

import { markdownSanitizeSchema } from '~/lib/markdownSanitizeSchema';
import { mailboxPage, usePageHeading } from '~/components/navigation';

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

const CaraInboundEmailReference = ({ className }: { className?: string }) => {
  const email = getCaraInboundAIEmailAddress();
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    void navigator.clipboard.writeText(email).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className={clsx(
        'flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400',
        className
      )}
    >
      <EnvelopeIcon
        className="size-4 shrink-0 text-neutral-400 dark:text-neutral-500"
        aria-hidden
      />
      <Code>{email}</Code>
      <button
        type="button"
        onClick={handleCopy}
        className="rounded p-1 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-white/10 hover:text-neutral-700 dark:hover:text-neutral-300"
        aria-label="Copy email address"
        title="Copy email address"
      >
        {copied ? (
          <CheckIcon className="size-4 text-green-600 dark:text-green-400" aria-hidden />
        ) : (
          <ClipboardDocumentIcon className="size-4" aria-hidden />
        )}
      </button>
    </div>
  );
};

export const MailboxPage = () => {
  const { title, description } = usePageHeading(mailboxPage);

  return (
    <PageTitle eyebrow={mailboxPage.name} title={title} description={description}>
      <PageOutlet />
    </PageTitle>
  );
};

export const EmailThreadsPage = () => {
  const outlet = useOutlet();
  const loader = useLoaderData() as
    | { data: Promise<{ emailThreads?: EmailThreadFieldsFragment[] }> }
    | undefined;

  return (
    <>
      <div className="space-y-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <form className="min-w-0 flex-1">
            <Fieldset>
              <FieldGroup>
                <div className="flex gap-4">
                  <Field className="w-full max-w-md">
                    <Label className="sr-only" data-slot="none">
                      Filter table
                    </Label>
                    <InputGroup>
                      <MagnifyingGlassIcon className="h-4 w-4" />
                      <Input type="search" name="search" placeholder="Start typing to filter..." />
                    </InputGroup>
                  </Field>
                  <Field>
                    <Label className="sr-only" data-slot="none">
                      Filter by status
                    </Label>
                    <Select name="status" value="">
                      <option value="" disabled>
                        Filter by status
                      </option>
                      <option value="Pending">In Progress</option>
                      <option value="Assigned to AI">Completed</option>
                      <option value="Ready for Review">Error</option>
                    </Select>
                  </Field>
                </div>
              </FieldGroup>
            </Fieldset>
          </form>
          <CaraInboundEmailReference className="shrink-0 self-start sm:self-center" />
        </div>

        <React.Suspense fallback={<EmailThreadsPageLoader />}>
          <Await resolve={loader?.data}>
            {({ emailThreads }) => (
              <div className="px-4 sm:px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
                {!emailThreads?.length && (
                  <div className="text-center py-12">
                    <EnvelopeIcon className="mx-auto h-12 w-12 text-neutral-400 dark:text-neutral-500" />
                    <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
                      No email threads found
                    </h3>
                    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
                      Cara hasn't been a part of any email conversations yet.
                    </p>
                  </div>
                )}
                {!!emailThreads?.length && (
                  <Table
                    dense
                    bleed
                    className="[--gutter:theme(spacing.4)] sm:[--gutter:theme(spacing.6)]"
                  >
                    <TableHead>
                      <TableRow>
                        <TableHeader className="py-4">Customer</TableHeader>
                        <TableHeader className="py-4">Subject</TableHeader>
                        <TableHeader className="py-4">Status</TableHeader>
                        <TableHeader className="py-4">Waiting For</TableHeader>
                        <TableHeader className="py-4">Last Update</TableHeader>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {emailThreads.map((emailThread) => (
                        <TableRow
                          key={emailThread.id}
                          href={`/app/mailbox/inbox/${emailThread.id}`}
                        >
                          <TableCell>
                            {emailThread.account?.account.name && (
                              <div className="font-medium flex gap-1 items-center">
                                <div>{emailThread.account.account.name} </div>
                                <div>
                                  <CheckBadgeIcon className="size-4 text-blue-400" />
                                </div>
                              </div>
                            )}
                            {!emailThread.account?.account.name && !!emailThread.fromName && (
                              <div className="font-medium">{emailThread.fromName}</div>
                            )}
                            {!emailThread.account?.account.name && !emailThread.fromName && (
                              <div className="font-medium text-neutral-500 dark:text-neutral-400">
                                —
                              </div>
                            )}
                            {emailThread.fromEmail && (
                              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                                {emailThread.fromEmail}
                              </div>
                            )}
                            {!emailThread.fromEmail && (
                              <div className="text-sm text-neutral-500 dark:text-neutral-400">
                                —
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {emailThread.subject && <div>{emailThread.subject}</div>}
                            {!emailThread.subject && (
                              <div className="text-neutral-500 dark:text-neutral-400">—</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <EmailThreadStateBadge state={emailThread.state} />
                          </TableCell>
                          <TableCell>
                            {emailThread.waitingFor && (
                              <EmailThreadParticipantBadge role={emailThread.waitingFor} />
                            )}
                            {!emailThread.waitingFor && (
                              <div className="text-neutral-500 dark:text-neutral-400">—</div>
                            )}
                          </TableCell>
                          <TableCell>
                            {new Intl.DateTimeFormat('en-US', {
                              dateStyle: 'short',
                              timeStyle: 'short'
                            }).format(new Date(emailThread.updatedAt))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </Await>
        </React.Suspense>
      </div>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet, {})}</AnimatePresence>
    </>
  );
};

const EmailThreadsPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900 dark:text-white">
          Loading inbox...
        </h3>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          This might take a moment.
        </p>
      </div>
    </div>
  );
};

const EmailThreadStateBadge = (props: { state: EmailThreadState }) => {
  return (
    <Badge
      // eslint-disable-next-line
      // @ts-ignore
      color={
        {
          [EmailThreadState.Active]: 'blue',
          [EmailThreadState.Ended]: 'green'
        }[props.state] || 'neutral'
      }
    >
      {titleCase(props.state)}
    </Badge>
  );
};

const EmailThreadParticipantBadge = (props: { role: EmailSenderRole }) => {
  return (
    <Badge
      // eslint-disable-next-line
      // @ts-ignore
      color={
        {
          [EmailSenderRole.Client]: 'blue',
          [EmailSenderRole.Participant]: 'blue',
          [EmailSenderRole.Agent]: 'amber',
          [EmailSenderRole.Cara]: 'green'
        }[props.role] || 'neutral'
      }
    >
      {titleCase(props.role === EmailSenderRole.Participant ? EmailSenderRole.Client : props.role)}
    </Badge>
  );
};

export const EmailThreadPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const loader = useLoaderData() as
    | {
        data: Promise<{
          emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
        }>;
      }
    | undefined;

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/mailbox/inbox');
    } else {
      navigate(-1);
    }
  };

  return (
    <MountableSlideout onClose={onClose} title="Email Thread">
      <React.Suspense fallback={<></>}>
        <Await resolve={loader?.data}>
          {(data) => data?.emailThread && <EmailThread emailThread={data.emailThread} />}
        </Await>
      </React.Suspense>
    </MountableSlideout>
  );
};

const EmailThread = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <dl>
      <EmailThreadProperties emailThread={props.emailThread} />
      <EmailThreadAccountSummary emailThread={props.emailThread} />
      {props.emailThread.summary && <EmailThreadSummary emailThread={props.emailThread} />}
      {props.emailThread.workspace && <EmailThreadWorkspace emailThread={props.emailThread} />}
      {props.emailThread.serviceRequests.length > 0 && (
        <EmailThreadServiceRequests emailThread={props.emailThread} />
      )}
      {(props.emailThread?.participantEmails?.length || 0) > 0 && (
        <EmailThreadConversation emailThread={props.emailThread} />
      )}
    </dl>
  );
};

const EmailThreadProperties = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  const properties = [
    {
      title: 'Subject',
      value: props.emailThread.subject
    },
    {
      title: 'Status',
      value: <EmailThreadStateBadge state={props.emailThread.state} />
    },
    {
      title: 'Waiting For',
      value: props.emailThread.waitingFor ? (
        <EmailThreadParticipantBadge role={props.emailThread.waitingFor} />
      ) : (
        <div className="text-neutral-500 dark:text-neutral-400">—</div>
      )
    },
    {
      title: 'Started At',
      value: new Intl.DateTimeFormat('en-US', {
        dateStyle: 'short',
        timeStyle: 'short'
      }).format(new Date(props.emailThread.createdAt))
    },
    {
      title: 'Last Update',
      value: new Intl.DateTimeFormat('en-US', {
        dateStyle: 'short',
        timeStyle: 'short'
      }).format(new Date(props.emailThread.updatedAt))
    }
  ].filter((property) => !!property.value);

  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
      {properties.map((property) => (
        <div key={property.title} className="sm:grid sm:grid-cols-3 sm:gap-4">
          <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">
            {property.title}
          </dt>
          <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 sm:col-span-2">
            {property.value}
          </dd>
        </div>
      ))}
    </div>
  );
};

const EmailThreadAccountSummary = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
      <div className="sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Client</dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 sm:col-span-2">
          {props.emailThread.fromName || (
            <div className="text-neutral-500 dark:text-neutral-400">—</div>
          )}
        </dd>
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Account</dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 sm:col-span-2">
          {props.emailThread.account?.account.name && (
            <Link to={`/app/accounts/${props.emailThread.account.id}`} className="hover:underline">
              <div className="flex items-center gap-1">
                {props.emailThread.account.account.name}
                <ArrowTopRightOnSquareIcon className="size-4" />
              </div>
            </Link>
          )}
          {!props.emailThread.account?.account.name && (
            <div className="text-neutral-500 dark:text-neutral-400">—</div>
          )}
        </dd>
      </div>
      <div className="sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Email Address</dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 sm:col-span-2">
          {props.emailThread.fromEmail || (
            <div className="text-neutral-500 dark:text-neutral-400">—</div>
          )}
        </dd>
      </div>
    </div>
  );
};

const EmailThreadSummary = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Summary</dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300">
          {props.emailThread.summary}
        </dd>
      </div>
    </div>
  );
};

const ServiceRequestBadge = (props: { state: ServiceRequestState }) => {
  return (
    <Badge
      // eslint-disable-next-line
      // @ts-ignore
      color={
        {
          [ServiceRequestState.Open]: 'amber',
          [ServiceRequestState.Closed]: 'green'
        }[props.state] || 'neutral'
      }
    >
      {props.state}
    </Badge>
  );
};

const EmailThreadServiceRequests = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
      <div className="flex flex-col sm:gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Service Requests</dt>
        <dd className="text-sm/6 text-neutral-900 dark:text-white">
          <ul
            role="list"
            className="divide-y divide-neutral-100 dark:divide-neutral-700 rounded-md border border-neutral-200 dark:border-neutral-700"
          >
            {props.emailThread.serviceRequests.map((serviceRequest) => (
              <li key={serviceRequest.id} className="flex justify-between py-4 pl-4 pr-5 text-sm/6">
                <div className="flex w-0 flex-1">
                  <BellIcon
                    aria-hidden="true"
                    className="size-6 shrink-0 text-neutral-400 dark:text-neutral-500"
                  />
                  <div className="ml-4 min-w-0">
                    <Link
                      to={`/app/servicing/${serviceRequest.id}/overview`}
                      className="truncate font-medium hover:underline"
                    >
                      {titleCase(serviceRequest.type)}
                    </Link>
                    <div className="text-neutral-500 dark:text-neutral-400">
                      {serviceRequest.details ||
                        serviceRequest.question?.question ||
                        serviceRequest.cancelPolicy?.reason}
                    </div>
                  </div>
                </div>
                <div className="ml-4 shrink-0">
                  <ServiceRequestBadge state={serviceRequest.state} />
                </div>
              </li>
            ))}
          </ul>
        </dd>
      </div>
    </div>
  );
};

const WorkspaceBadge = (props: { state: CaraWorkspaceState }) => {
  return (
    <Badge
      // eslint-disable-next-line
      // @ts-ignore
      color={
        {
          [CaraWorkspaceState.Created]: 'neutral',
          [CaraWorkspaceState.Idle]: 'green',
          [CaraWorkspaceState.Working]: 'blue',
          [CaraWorkspaceState.WaitingForInput]: 'amber'
        }[props.state] || 'neutral'
      }
    >
      {props.state}
    </Badge>
  );
};

const EmailThreadWorkspace = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
      <div className="flex flex-col sm:gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Workspace</dt>
        <dd className="text-sm/6 text-neutral-900 dark:text-white">
          <ul
            role="list"
            className="divide-y divide-neutral-100 dark:divide-neutral-700 rounded-md border border-neutral-200 dark:border-neutral-700"
          >
            <li className="flex justify-between py-4 pl-4 pr-5 text-sm/6">
              <div className="flex w-0 flex-1">
                <BellIcon
                  aria-hidden="true"
                  className="size-6 shrink-0 text-neutral-400 dark:text-neutral-500"
                />
                <div className="ml-4 min-w-0">
                  <Link
                    to={`/app/workspaces/${props.emailThread.workspace?.id}`}
                    className="truncate font-medium hover:underline"
                  >
                    Go to Workspace
                  </Link>
                  <div className="text-neutral-500 dark:text-neutral-400">
                    {props.emailThread.workspace?.title}
                  </div>
                </div>
              </div>
              <div className="ml-4 shrink-0">
                <WorkspaceBadge
                  state={props.emailThread.workspace?.state || CaraWorkspaceState.Created}
                />
              </div>
            </li>
          </ul>
        </dd>
      </div>
    </div>
  );
};

const EmailThreadConversation = (props: {
  emailThread: EmailThreadFieldsFragment & EmailThreadConversationFieldsFragment;
}) => {
  return (
    <div className="flex flex-col sm:gap-1.5 gap-3 pb-6 pt-6 border-t border-neutral-200 dark:border-neutral-700">
      <div className="flex flex-col gap-1.5">
        <dt className="text-sm/6 font-medium text-neutral-900 dark:text-white">Transcript</dt>
        <dd className="text-sm/6 text-neutral-700 dark:text-neutral-300 flex flex-col gap-2.5">
          {props.emailThread.participantEmails.map((message) => {
            if (
              [EmailSenderRole.Client, EmailSenderRole.Participant].includes(message.senderRole)
            ) {
              return <UserMessage message={message} />;
            }
            if (message.senderRole === EmailSenderRole.Agent) {
              return <AgentMessage message={message} />;
            }
            if (message.senderRole === EmailSenderRole.Cara) {
              return <AIMessage message={message} />;
            }
          })}
        </dd>
      </div>
    </div>
  );
};

const UserMessage = (props: { message: EmailFieldsFragment }) => {
  return (
    <div className="bg-neutral-100 dark:bg-neutral-700 p-4 rounded-lg">
      <div className="uppercase text-xs text-neutral-500 dark:text-neutral-400 font-medium">
        {props.message.from.name}
      </div>
      <pre className="text-sm/6 font-body font-normal whitespace-pre-wrap text-neutral-700 dark:text-neutral-300 mt-2">
        {props.message.body}
      </pre>
    </div>
  );
};

const AgentMessage = (props: { message: EmailFieldsFragment }) => {
  return (
    <div className="bg-neutral-100 dark:bg-neutral-700 p-4 rounded-lg">
      <div className="uppercase text-xs text-neutral-500 dark:text-neutral-400 font-medium">
        {props.message.from.name}
      </div>
      <pre className="text-sm/6 font-body font-normal whitespace-pre-wrap text-neutral-700 dark:text-neutral-300 mt-2">
        {props.message.body}
      </pre>
    </div>
  );
};

const AIMessage = (props: { message: EmailFieldsFragment }) => {
  return (
    <div className="bg-blue-50 dark:bg-blue-500/10 p-4 rounded-lg">
      <div className="uppercase text-xs text-blue-500 dark:text-blue-400 font-medium">
        {props.message.from.name}
      </div>
      <div className="mt-2 text-neutral-700 dark:text-neutral-300 email-markdown-container">
        <Markdown
          remarkPlugins={[[remarkGfm, {}]]}
          rehypePlugins={[
            [rehypeRaw, { tagFilter: true }],
            [rehypeSanitize, markdownSanitizeSchema]
          ]}
        >
          {props.message.body}
        </Markdown>
      </div>
    </div>
  );
};
